import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  TextField,
  Checkbox,
  Button,
  Container,
  Card,
  CardContent,
  Box,
  Grid,
  Divider,
  FormControlLabel,
  Alert,
  Link as MuiLink,
} from "@mui/material";
import config from "../../utils/config";
import PhoneInput from "react-phone-input-2";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { toast } from "react-toastify";
import moment from "moment";
import { loadStripe } from "@stripe/stripe-js";
import fieldValidator from "../../utils/field-validator";
import { useEffect, useState } from "react";
import { useDebouncedCallback } from "use-debounce";
import axios from "axios";
import { useParams } from "react-router-dom";
import universities from "../../utils/universities";
import countries from "../../utils/countries";
import mobilityPrograms from "../../utils/mobility-programs";
import esnCountryConfig from "./esn-country-config";
import { DateTimePicker } from "@mui/x-date-pickers";
import { validateEmail } from "../../utils/email-validator";

function StudentRequest() {
  const { esnCountry } = useParams();

  const [region, setRegion] = useState("");
  const [organization, setOrganization] = useState("");
  const [university, setUniversity] = useState("");
  const [name, setName] = useState("");
  const [gender, setGender] = useState("");
  const [passport, setPassport] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [birthdate, setBirthdate] = useState(null);
  const [address, setAddress] = useState("");
  const [country, setCountry] = useState("");
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [photosAccepted, setPhotosAccepted] = useState(false);
  const [newsletterAccepted, setNewsletterAccepted] = useState(false);
  const [program, setProgram] = useState("");
  const [programDuration, setProgramDuration] = useState("");
  const [vat, setVat] = useState("");
  const [customQuestions, setCustomQuestions] = useState({});

  // Picker data
  const [allRegions, setAllRegions] = useState(new Set());
  const [universitiesByRegion, setUniversitiesByRegion] = useState({});
  const [organizationsByRegion, setOrganizationsByRegion] = useState({});
  const [organizationsByRegionAndUni, setOrganizationsByRegionAndUni] = useState({});
  const [possibleOrgs, setPossibleOrgs] = useState({});

  useEffect(() => {
    let newAllRegions = new Set();
    let newUniversitiesByRegion = {};
    let newOrganizationsByRegion = {};
    let newOrganizationsByRegionAndUni = {};

    for (let entry of Object.entries(universities[esnCountry])) {
      let org = entry[0];
      let regionDict = entry[1];

      for (let reg of Object.keys(regionDict)) {
        // All Regions
        newAllRegions.add(reg);

        // Universities by region
        if (!(reg in newUniversitiesByRegion)) {
          newUniversitiesByRegion[reg] = new Set();
        }

        for (let uni of regionDict[reg]) {
          newUniversitiesByRegion[reg].add(uni);
        }

        // Organizations by region
        if (!(reg in newOrganizationsByRegion)) {
          newOrganizationsByRegion[reg] = new Set();
        }
        newOrganizationsByRegion[reg].add(org);

        // Organizations by region and university
        if (!(reg in newOrganizationsByRegionAndUni)) {
          newOrganizationsByRegionAndUni[reg] = {};
        }

        for (let unis of Object.values(regionDict)) {
          for (let uni of unis) {
            if (!(uni in newOrganizationsByRegionAndUni[reg])) {
              newOrganizationsByRegionAndUni[reg][uni] = new Set();
            }

            newOrganizationsByRegionAndUni[reg][uni].add(org);
          }
        }
      }
    }

    // Set data
    setAllRegions(newAllRegions);
    setUniversitiesByRegion(newUniversitiesByRegion);
    setOrganizationsByRegion(newOrganizationsByRegion);
    setOrganizationsByRegionAndUni(newOrganizationsByRegionAndUni);
  }, [esnCountry]); // eslint-disable-line

  const getPossibleOrganizations = async (orgIds) => {
    if (orgIds == null) return;

    let result = {};

    try {
      for (let orgId of orgIds) {
        const res = await axios.get(`${config.API_ENDPOINT}/organizations/${orgId}`);
        result[res.data._id] = res.data;
      }
    } catch (err) {
      toast.error("An unexpected error has occurred");
      return;
    }

    setPossibleOrgs(result);
  };

  const loadDefaultAnswers = (organization) => {
    if (organization == null || organization === "") return;

    const org = possibleOrgs[organization];
    if (org == null) return;

    if (org.customEsnCardQuestions == null) return;

    let result = {};

    for (let q of org.customEsnCardQuestions) {
      if (q.type === "select-multi") {
        result[q.id] = [];
      } else {
        result[q.id] = "";
      }
    }

    setCustomQuestions(result);
  };

  const validateFields = () => {
    if (
      !fieldValidator.validateSimple([
        region,
        organization,
        university,
        name,
        gender,
        passport,
        email,
        phone,
        birthdate,
        address,
        country,
        termsAccepted,
        program,
        programDuration,
      ]) ||
      !customQuestionsFilled()
    ) {
      toast.error("You must fill all required fields.");
      return false;
    }

    if (!termsAccepted) {
      toast.warning("Terms and conditions must be accepted to proceed.");
      return false;
    }

    if (!validateEmail(email)) {
      toast.error("Invalid email");
      return false;
    }

    return true;
  };

  const customQuestionsFilled = () => {
    if (organization == null || organization === "") return true;

    const org = possibleOrgs[organization];
    if (org == null) return true;

    if (org.customEsnCardQuestions == null) return true;

    for (let q of org.customEsnCardQuestions) {
      if (!q.required) continue;

      if (
        customQuestions[q.id] == null ||
        customQuestions[q.id] === "" ||
        (Array.isArray(customQuestions[q.id]) && customQuestions[q.id].length === 0)
      ) {
        return false;
      }
    }

    return true;
  };

  const resetState = () => {
    setRegion("");
    setOrganization("");
    setUniversity("");
    setName("");
    setGender("");
    setPassport("");
    setEmail("");
    setPhone("");
    setBirthdate(null);
    setAddress("");
    setCountry("");
    setTermsAccepted(false);
    setPhotosAccepted(true);
    setNewsletterAccepted(true);
    setProgram("");
    setVat("");
    setProgramDuration("");
    setPossibleOrgs({});
    setCustomQuestions({});
  };

  const submitRequest = useDebouncedCallback(
    (pay) => {
      if (!validateFields()) return;

      const request = {
        name,
        gender,
        passport,
        email,
        phone,
        birthdate: birthdate.valueOf(),
        address: address.trim(),
        country,
        organization,
        university,
        photos: photosAccepted,
        newsletter: newsletterAccepted,
        timestamp: moment().valueOf(),
        vat,
        program,
        programDuration,
        customQuestions,
      };

      if (!pay) {
        axios
          .post(`${config.API_ENDPOINT}/student-requests/create`, request)
          .then(() => {
            toast.success("Request submitted. Please check your inbox.");
            resetState();
          })
          .catch((err) => {
            switch (err.response?.status) {
              case 400:
                toast.error("Invalid email format");
                break;
              case 409:
                toast.error("Email/Passport already in use.");
                break;
              default:
                toast.error("An unexpected error has occurred");
                break;
            }
          });
      } else {
        axios
          .post(`${config.API_ENDPOINT}/checkout/esncard`, {
            organization: request.organization,
            cancel: `${config.WEB_ENDPOINT}/iframes/student-request/${esnCountry}`,
            success: `${config.WEB_ENDPOINT}/iframes/purchase/esncard/${request.organization}`,
            metadata: {
              type: "card",
              data: request,
            },
          })
          .then(async (res) => {
            const result = res.data;

            switch (result.type) {
              case "stripe":
                const stripe = await loadStripe(result.publicKey);

                const redirect = await stripe.redirectToCheckout({
                  sessionId: result.session,
                });

                if (redirect.error) {
                  toast.error("Error opening the checkout");
                  console.error(redirect.error);
                }
                break;
              case "redsys":
                // Create a hidden form element programatically and submit it
                const form = document.createElement("form");
                form.method = "POST";
                form.action = result.url;
                form.style = "display: none";

                for (let key in result.body) {
                  const input = document.createElement("input");
                  input.type = "hidden";
                  input.name = key;
                  input.value = result.body[key];
                  form.appendChild(input);
                }

                document.body.appendChild(form);
                form.submit();
                break;
              default:
                toast.error("An unexpected error has occurred");
                break;
            }
          })
          .catch((err) => {
            switch (err.response?.status) {
              case 400:
                toast.error("Invalid request. Please verify your data.");
                break;
              case 401:
                toast.warning("This organization does not support online payments");
                break;
              case 409:
                toast.error("Email/Passport already in use.");
                break;
              default:
                toast.error("An unexpected error has occurred");
                break;
            }
          });
      }
    },
    500,
    { leading: true }
  );

  const extraQuestions = () => {
    if (organization == null || organization === "") return null;

    const org = possibleOrgs[organization];
    if (org == null) return null;

    if (org.customEsnCardQuestions == null || org.customEsnCardQuestions.length === 0) {
      return null;
    }

    return (
      <>
        {org.customEsnCardQuestions.map((q) => {
          switch (q.type) {
            case "select-multi":
            case "select":
              return (
                <Grid item xs={12} key={q.id}>
                  <FormControl fullWidth variant="outlined" required={q.required}>
                    <InputLabel sx={{ backgroundColor: "#fff" }}>{q.question}</InputLabel>
                    <Select
                      value={customQuestions[q.id]}
                      multiple={q.type === "select-multi"}
                      onChange={(e) => {
                        let questions = { ...customQuestions };
                        questions[q.id] = e.target.value;
                        setCustomQuestions(questions);
                      }}
                    >
                      {q.values.map((value) => (
                        <MenuItem key={value} value={value}>
                          {value}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              );
            case "text":
              return (
                <Grid item xs={12} key={q.id}>
                  <TextField
                    variant="outlined"
                    required={q.required}
                    fullWidth
                    label={q.question}
                    name={q.id}
                    type="text"
                    value={customQuestions[q.id]}
                    onChange={(e) => {
                      let questions = { ...customQuestions };
                      questions[q.id] = e.target.value;
                      setCustomQuestions(questions);
                    }}
                  />
                </Grid>
              );
            case "datetime":
              return (
                <Grid item xs={12} key={q.id}>
                  <DateTimePicker
                    variant="outlined"
                    required={q.required}
                    label={q.question}
                    name={q.id}
                    value={moment(customQuestions[q.id])}
                    onChange={(val) => {
                      let questions = { ...customQuestions };
                      questions[q.id] = moment(val).valueOf();
                      setCustomQuestions(questions);
                    }}
                    ampm={false}
                    slotProps={{ textField: { fullWidth: true } }}
                  />
                </Grid>
              );
            default:
              return null;
          }
        })}
      </>
    );
  };

  const studentInfoUI = () => {
    if (region == null || region === "") return null;
    if (university == null || university === "") return null;
    if (organization == null || organization === "") return null;

    const org = possibleOrgs[organization];
    if (org == null) return null;

    return (
      <Box>
        {/* Info Summary */}
        <Card sx={{ mb: 3, backgroundColor: "#f0f8ff", borderLeft: "4px solid #00aeef" }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
              🎫 Registration Summary
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              You are registering an ESNcard for the section <strong>{org.name}</strong>
            </Typography>
            <Typography variant="h6" sx={{ color: "#00aeef", fontWeight: 700, mb: 2 }}>
              Price: {org.esnCardPrice.toFixed(2)}€
            </Typography>
            <Divider sx={{ my: 1.5 }} />
            <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 1.5 }}>
              ✓ Confirmation will be sent to your email address<br/>
              ✓ Check spam/promotions folder if it doesn't arrive<br/>
              ✓ You'll receive instructions on validating your purchase
            </Typography>
          </CardContent>
        </Card>

        {/* Personal Information Form */}
        <Card sx={{ mb: 3 }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
              Step 2: Personal Information
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  variant="outlined"
                  required
                  fullWidth
                  label="Full Name"
                  name="name"
                  type="text"
                  autoComplete="off"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth variant="outlined" required>
                  <InputLabel sx={{ backgroundColor: "#fff" }}>Gender</InputLabel>
                  <Select value={gender} onChange={(e) => setGender(e.target.value)}>
                    <MenuItem value="male">Male</MenuItem>
                    <MenuItem value="female">Female</MenuItem>
                    <MenuItem value="other">Other</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  variant="outlined"
                  required
                  fullWidth
                  label="ID/Passport"
                  name="passport"
                  type="text"
                  autoComplete="off"
                  value={passport}
                  onChange={(e) => setPassport(e.target.value.trim())}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  variant="outlined"
                  required
                  fullWidth
                  label="Email"
                  name="email"
                  type="email"
                  autoComplete="off"
                  value={email}
                  onChange={(e) => setEmail(e.target.value.trim())}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DatePicker
                  label="Date of Birth"
                  format="DD/MM/YYYY"
                  timezone="UTC"
                  value={birthdate}
                  onChange={(date) => setBirthdate(date)}
                  slotProps={{ textField: { required: true, fullWidth: true } }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box sx={{ mb: 2 }}>
                  <PhoneInput
                    placeholder="Enter phone number"
                    value={phone}
                    onChange={(number) => setPhone(`+${number}`)}
                    containerStyle={{ width: "100%" }}
                  />
                </Box>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  variant="outlined"
                  required
                  fullWidth
                  label="Address"
                  name="address"
                  type="text"
                  autoComplete="off"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth variant="outlined" required>
                  <InputLabel sx={{ backgroundColor: "#fff" }}>Nationality</InputLabel>
                  <Select value={country} onChange={(e) => setCountry(e.target.value)}>
                    {countries.map((country) => (
                      <MenuItem key={country.name} value={country.name}>
                        {country.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth variant="outlined" required>
                  <InputLabel sx={{ backgroundColor: "#fff" }}>Mobility Program</InputLabel>
                  <Select value={program} onChange={(e) => setProgram(e.target.value)}>
                    {mobilityPrograms.map((program) => (
                      <MenuItem key={program} value={program}>
                        {program}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth variant="outlined" required>
                  <InputLabel sx={{ backgroundColor: "#fff" }}>Duration of the Program</InputLabel>
                  <Select value={programDuration} onChange={(e) => setProgramDuration(e.target.value)}>
                    <MenuItem value={"<1 month"}>{"<1 month"}</MenuItem>
                    <MenuItem value={"1-3 months"}>1-3 months</MenuItem>
                    <MenuItem value={"4-6 months (1 Semester)"}>{"4-6 months (1 Semester)"}</MenuItem>
                    <MenuItem value={"7-12 months (1 Year)"}>{"7-12 months (1 Year)"}</MenuItem>
                    <MenuItem value={"1-2 years"}>{"1-2 years"}</MenuItem>
                    <MenuItem value={">2 years"}>{">2 years"}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              {org.showVAT && (
                <Grid item xs={12}>
                  <TextField
                    variant="outlined"
                    fullWidth
                    label="VAT Number (Optional)"
                    name="vat"
                    type="text"
                    value={vat}
                    onChange={(e) => setVat(e.target.value.trim())}
                  />
                  <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 1 }}>
                    Include country code. (e.g. PT123456789) Only required for invoices.
                  </Typography>
                </Grid>
              )}
            </Grid>
          </CardContent>
        </Card>

        {/* Extra Questions */}
        {extraQuestions() && (
          <Card sx={{ mb: 3 }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                Additional Information
              </Typography>
              <Grid container spacing={2}>
                {extraQuestions()}
              </Grid>
            </CardContent>
          </Card>
        )}

        {/* Terms & Consent */}
        <Card sx={{ mb: 3 }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
              Agreements & Consents
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              <FormControlLabel
                control={<Checkbox checked={termsAccepted} onChange={(e) => setTermsAccepted(e.target.checked)} />}
                label={
                  <Typography variant="body2">
                    I agree to the{" "}
                    <MuiLink target="_blank" rel="noopener noreferrer" href={`/terms-conditions/${organization}`} sx={{ fontWeight: 700 }}>
                      terms and conditions
                    </MuiLink>
                    {" "} *
                  </Typography>
                }
              />
              <FormControlLabel
                control={<Checkbox checked={photosAccepted} onChange={(e) => setPhotosAccepted(e.target.checked)} />}
                label={
                  <Typography variant="body2">
                    I authorize the organization to take pictures during activities for promotion purposes
                  </Typography>
                }
              />
              <FormControlLabel
                control={<Checkbox checked={newsletterAccepted} onChange={(e) => setNewsletterAccepted(e.target.checked)} />}
                label={
                  <Typography variant="body2">
                    I authorize the organization to send me email updates about new activities and promotions
                  </Typography>
                }
              />
            </Box>
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <Card>
          <CardContent sx={{ p: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Button
                  fullWidth
                  variant="outlined"
                  sx={{
                    py: 1.5,
                    textTransform: "none",
                    fontWeight: 700,
                    borderColor: "#00aeef",
                    color: "#00aeef",
                  }}
                  onClick={() => submitRequest(false)}
                >
                  Pay in Office
                </Button>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Button
                  fullWidth
                  variant="contained"
                  sx={{
                    py: 1.5,
                    backgroundColor: "#00aeef",
                    color: "white",
                    textTransform: "none",
                    fontWeight: 700,
                    "&:hover": { backgroundColor: "#0095d1" },
                  }}
                  onClick={() => submitRequest(true)}
                >
                  Pay Now
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Box>
    );
  };

  const regionPicker = () => {
    return (
      <Box sx={{ mb: 1 }}>
        <Typography variant="body2" sx={{ color: "text.secondary", mb: 1 }}>
          First, select your region.
        </Typography>
        <FormControl fullWidth variant="outlined" required>
          <InputLabel sx={{ backgroundColor: "#fff" }} id="region-label">
            Region
          </InputLabel>
          <Select
            labelId="region-label"
            label="Region"
            value={region}
            onChange={(e) => {
              let reg = e.target.value;
              setRegion(reg);
              setUniversity("");
              setOrganization("");

              if (esnCountryConfig[esnCountry].orgFirst) {
                setOrganization("");
                setPossibleOrgs({});
                getPossibleOrganizations(organizationsByRegion[reg]);
              }
            }}
          >
            {[...allRegions].sort().map((region) => (
              <MenuItem key={region} value={region}>
                {region}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
    );
  };

  const secondPicker = () => {
    if (region == null || region === "") {
      return null;
    }

    if (esnCountryConfig[esnCountry].orgFirst) {
      return organizationPicker();
    } else {
      return universityPicker(universitiesByRegion[region]);
    }
  };

  const thirdPicker = () => {
    if (region == null || region === "") {
      return null;
    }

    if (esnCountryConfig[esnCountry].orgFirst) {
      if (organization == null || organization === "") {
        return null;
      }

      return universityPicker(universities[esnCountry][organization][region]);
    } else {
      if (university == null || university === "") {
        return null;
      }

      return organizationPicker();
    }
  };

  const universityPicker = (universitiesList) => {
    return (
      <Box sx={{ mb: 1 }}>
        <Typography variant="body2" sx={{ color: "text.secondary", mb: 1 }}>
          Now, select your university.
        </Typography>
        <FormControl fullWidth variant="outlined" required>
          <InputLabel sx={{ backgroundColor: "#fff" }}>University</InputLabel>
          <Select
            value={university}
            onChange={(e) => {
              let uni = e.target.value;
              setUniversity(uni);

              if (!esnCountryConfig[esnCountry].orgFirst) {
                setOrganization("");
                setPossibleOrgs({});
                getPossibleOrganizations(organizationsByRegionAndUni[region][uni]);
              }
            }}
          >
            {[...universitiesList].sort().map((uni) => (
              <MenuItem key={uni} value={uni}>
                {uni}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
    );
  };

  const organizationPicker = () => {
    return (
      <Box sx={{ mb: 1 }}>
        <Typography variant="body2" sx={{ color: "text.secondary", mb: 1 }}>
          Now, select your ESN organization.
        </Typography>
        <FormControl fullWidth variant="outlined" required>
          <InputLabel sx={{ backgroundColor: "#fff" }}>ESN Organization</InputLabel>
          <Select
            value={organization}
            onChange={(e) => {
              setOrganization(e.target.value);

              loadDefaultAnswers(e.target.value);

              if (esnCountryConfig[esnCountry].orgFirst) {
                setUniversity("");
              }
            }}
          >
            {Object.values(possibleOrgs).sort((a, b) => a.name.localeCompare(b.name)).map((org) => (
              <MenuItem key={org._id} value={org._id}>
                {org.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
    );
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h4" sx={{ fontWeight: 700, color: "#00aeef", mb: 1 }}>
            Welcome to ESN!
          </Typography>
          <Typography variant="body1" sx={{ color: "text.secondary" }}>
            Let's get your ESNcard set up. Complete the form below to get started.
          </Typography>
        </CardContent>
      </Card>

      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
            Step 1: Location Selection
          </Typography>
          {regionPicker()}
          {secondPicker()}
          {thirdPicker()}
        </CardContent>
      </Card>

      {studentInfoUI()}

      {esnCountryConfig[esnCountry]?.supportEmail && (
        <Alert severity="info" sx={{ mt: 3 }}>
          <Typography variant="body2">
            <strong>Need help?</strong> Email us at{" "}
            <MuiLink href={`mailto:${esnCountryConfig[esnCountry].supportEmail}`} sx={{ fontWeight: 700 }}>
              {esnCountryConfig[esnCountry].supportEmail}
            </MuiLink>
          </Typography>
        </Alert>
      )}
    </Container>
  );
}

export default StudentRequest;
